import { useRef, useEffect, useCallback } from 'react';

export function HeartbeatECG({ completedTasks = 0, failedTasks = 0, queueHealth = 0.8 }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const dataRef = useRef({ points: [], lastBeat: 0, beatInterval: 2000 });

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    const data = dataRef.current;

    // Beat frequency based on tasks (more tasks = faster beat)
    data.beatInterval = Math.max(800, 2000 - completedTasks * 0.5);

    const now = Date.now();
    const timeSinceBeat = now - data.lastBeat;
    const beatProgress = timeSinceBeat / data.beatInterval;

    // Generate ECG point
    let y = 0;
    if (beatProgress < 0.08) {
      y = -0.1; // P wave
    } else if (beatProgress < 0.12) {
      y = 0;
    } else if (beatProgress < 0.16) {
      y = -0.3; // Q dip
    } else if (beatProgress < 0.22) {
      y = 0.9 * queueHealth; // R peak (height = health)
    } else if (beatProgress < 0.28) {
      y = -0.2; // S dip
    } else if (beatProgress < 0.4) {
      y = 0.15; // T wave
    } else {
      y = 0;
    }

    if (beatProgress >= 1) {
      data.lastBeat = now;
    }

    // Add point
    data.points.push(y);
    if (data.points.length > w) data.points.shift();

    // Clear
    ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
    ctx.fillRect(0, 0, w, h);

    // Draw ECG line
    ctx.beginPath();
    ctx.strokeStyle = failedTasks > 0 ? '#ef4444' : '#22c55e';
    ctx.lineWidth = 1.5;
    ctx.shadowColor = failedTasks > 0 ? '#ef4444' : '#22c55e';
    ctx.shadowBlur = 6;

    const midY = h / 2;
    const amp = h * 0.35;

    for (let i = 0; i < data.points.length; i++) {
      const x = i;
      const py = midY - data.points[i] * amp;
      if (i === 0) ctx.moveTo(x, py);
      else ctx.lineTo(x, py);
    }
    ctx.stroke();
    ctx.shadowBlur = 0;

    // Draw scan line
    const scanX = data.points.length;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(scanX, 0, 30, h);

    animRef.current = requestAnimationFrame(draw);
  }, [completedTasks, failedTasks, queueHealth]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    const ctx = canvas.getContext('2d');
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    canvas.width = rect.width;
    canvas.height = rect.height;

    dataRef.current.lastBeat = Date.now();
    animRef.current = requestAnimationFrame(draw);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [draw]);

  return (
    <div className="relative w-full" data-testid="brain-ecg">
      <canvas
        ref={canvasRef}
        className="w-full h-12 rounded-lg"
        style={{ background: 'rgba(0,0,0,0.3)' }}
      />
      <div className="absolute top-1 left-3 flex items-center gap-2 text-[10px] text-muted-foreground">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${failedTasks > 0 ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} />
        <span>{completedTasks.toLocaleString()} tâches/24h</span>
        {failedTasks > 0 && <span className="text-red-400">· {failedTasks} erreurs</span>}
      </div>
    </div>
  );
}
